import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { RegrelloMenuItemContent, RegrelloTooltip } from "@regrello/ui-core";
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";
import { useMount } from "react-use";

import { ValidationRules } from "../../../../../../../constants/globalConstants";
import type { ConfigureSpectrumFieldFormFormFields } from "../../../../../../../ui2/fields/configure/_internal/ConfigureSpectrumFieldForm";
import { RegrelloControlledFormFieldNumber } from "../../../../formFields/controlled/RegrelloControlledFormFieldNumber";
import { RegrelloControlledFormFieldSelect } from "../../../../formFields/controlled/RegrelloControlledFormFieldSelect";
import { getDecimalPlaceMenuOptions, getDecimalPlaceOption } from "../../../utils/decimalPlaceOptionUtils";
import {
  getDisallowGroupingsFloatMenuOptions,
  getDisallowGroupingsFloatOption,
} from "../../../utils/disallowGroupingsFloatOptionUtils";
import { findValueConstraint } from "../../../utils/findValueConstraint";
import {
  FrontendValueConstraintRuleName,
  getValueConstraintDisplayName,
} from "../../../utils/spectrumFieldConstraintUtils";

/** The default number of decimal places for Number fields. */
const DEFAULT_NUMBER_DECIMAL_PLACES = 0;

/**
 * Renders inputs for the number field's various value constraints.
 */
export function SpectrumNumberValueConstraints({
  constraints,
  disabled,
  form,
}: {
  constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
  disabled: boolean;
  form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
}): React.ReactNode {
  const getFieldKey = (index: number) => `valueConstraints.${index}.args.0` as const;

  function findConstraint<TValue = number>(
    ruleName: FrontendValueConstraintRuleName,
    parseValue?: (value: string) => TValue,
  ) {
    return findValueConstraint<TValue>(form, constraints, ruleName, parseValue);
  }

  // Find constraints in the react-hook-form. Look for both INT-valued and FLOAT-valued constraints.
  const minIntConstraint = findConstraint(FrontendValueConstraintRuleName.MIN_INT);
  const maxIntConstraint = findConstraint(FrontendValueConstraintRuleName.MAX_INT);
  const minFloatConstraint = findConstraint(FrontendValueConstraintRuleName.MIN_FLOAT);
  const maxFloatConstraint = findConstraint(FrontendValueConstraintRuleName.MAX_FLOAT);
  const decimalPlacesConstraint = findConstraint(FrontendValueConstraintRuleName.DECIMAL_PLACES);
  const disallowGroupingsFloatConstraint = findConstraint(
    FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT,
    parseBoolean,
  );

  // Check if the field is a legacy INT-valued field.
  const propertyDataType = form.getValues("propertyDataType");
  const isLegacyIntValuedField = propertyDataType === "INT";

  useMount(() => {
    // Default the decimal places constraint to 0 for Number fields if it's not set.
    if (!Number.isFinite(decimalPlacesConstraint.value)) {
      form.setValue("valueConstraintsComplex.decimalPlaces", getDecimalPlaceOption(DEFAULT_NUMBER_DECIMAL_PLACES));
    }

    if (disallowGroupingsFloatConstraint.value == null && !isLegacyIntValuedField) {
      // Default the disallow_groupings_float constraint to false for float-valued numbers. Don't
      // bother changing for int-valued numbers, as we force-set the default for that elsewhere to
      // effectively force-disable groupings.
      form.setValue("valueConstraintsComplex.disallowGroupingsFloat", getDisallowGroupingsFloatOption(false));
    }
  });

  // Warn if any constraints are missing from a legacy INT-valued field.
  if (isLegacyIntValuedField && (minIntConstraint.index < 0 || maxIntConstraint.index < 0)) {
    console.warn(
      "SpectrumNumberFieldPluginDecorator: renderValueConstraints: minFloat or maxFloat constraint not found",
    );
    return null;
  }

  // Warn if any constraints are missing from a FLOAT-valued field.
  if (!isLegacyIntValuedField && (minFloatConstraint.index < 0 || maxFloatConstraint.index < 0)) {
    console.warn(
      "SpectrumNumberFieldPluginDecorator: renderValueConstraints: minFloat or maxFloat constraint not found",
    );
    return null;
  }
  if (!isLegacyIntValuedField && decimalPlacesConstraint.index < 0) {
    console.warn(
      "SpectrumNumberFieldPluginDecorator: renderValueConstraints: decimalPlaces constraint not found. Still rendering others.",
    );
  }
  if (!isLegacyIntValuedField && disallowGroupingsFloatConstraint.index < 0) {
    console.warn(
      "SpectrumNumberFieldPluginDecorator: renderValueConstraints: disallowGroupingsFloat constraint not found. Still rendering others.",
    );
  }

  // Determine which constraints to use based on whether it's an INT-valued or FLOAT-valued field.
  const minValueConstraint = isLegacyIntValuedField ? minIntConstraint : minFloatConstraint;
  const maxValueConstraint = isLegacyIntValuedField ? maxIntConstraint : maxFloatConstraint;

  const DISABLED_FEATURE_TOOLTIP_MAX_WIDTH = 300;

  return (
    <>
      {constraints.map((row, constraintIndex) => {
        if (row.constraint.valueConstraintRule === FrontendValueConstraintRuleName.DECIMAL_PLACES) {
          // Allow users to specify the number of decimal places.
          return (
            <RegrelloTooltip
              key={row.constraint.uuid}
              content={t`Decimal places are not supported for this field, because this field was created before decimal places were supported in Regrello.`}
              disabled={propertyDataType !== "INT"}
              maxWidth={DISABLED_FEATURE_TOOLTIP_MAX_WIDTH}
            >
              <div>
                <RegrelloControlledFormFieldSelect
                  controllerProps={{
                    control: form.control,
                    name: "valueConstraintsComplex.decimalPlaces",
                  }}
                  disabled={disabled || propertyDataType === "INT"}
                  getOptionLabel={(option) => option.label}
                  isFilterable={false}
                  label={getValueConstraintDisplayName(row.constraint)}
                  options={getDecimalPlaceMenuOptions()} // (clewis): This is O(N) but it's only a few items so it's fine.
                  renderOption={(option) => {
                    return <RegrelloMenuItemContent endElement={option.preview} text={option.label} />;
                  }}
                  renderSelectedValue={(option) => {
                    return option == null ? null : option.label;
                  }}
                  suppressOptionPadding={true}
                />
              </div>
            </RegrelloTooltip>
          );
        }

        if (row.constraint.valueConstraintRule === FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT) {
          // Allow users to toggle comma separators.
          return (
            <RegrelloTooltip
              key={row.constraint.uuid}
              content={t`Separators are not supported for this field, because this field was created before separators were supported in Regrello.`}
              disabled={propertyDataType !== "INT"}
              maxWidth={DISABLED_FEATURE_TOOLTIP_MAX_WIDTH}
            >
              <div>
                <RegrelloControlledFormFieldSelect
                  key={row.constraint.uuid}
                  controllerProps={{
                    control: form.control,
                    name: "valueConstraintsComplex.disallowGroupingsFloat",
                  }}
                  disabled={disabled || propertyDataType === "INT"}
                  getOptionLabel={(option) => option.label}
                  isFilterable={false}
                  label={getValueConstraintDisplayName(row.constraint)}
                  options={getDisallowGroupingsFloatMenuOptions()}
                  renderOption={(option) => {
                    return <RegrelloMenuItemContent endElement={option.preview} text={option.label} />;
                  }}
                  renderSelectedValue={(option) => {
                    if (option == null) {
                      return null;
                    }
                    const label = option.label;
                    const preview = option.preview;

                    return (
                      <span>
                        {label}{" "}
                        <span className="text-textMuted">
                          (<Trans>Example: {preview}</Trans>)
                        </span>
                      </span>
                    );
                  }}
                  suppressOptionPadding={true}
                />
              </div>
            </RegrelloTooltip>
          );
        }

        const fieldKey = getFieldKey(constraintIndex);

        return (
          <RegrelloControlledFormFieldNumber
            key={row.constraint.uuid}
            controllerProps={{
              control: form.control,
              name: fieldKey,
              rules:
                // If there's a min constraint, prevent the max value from going below it.
                constraintIndex === minValueConstraint.index && maxValueConstraint.value != null
                  ? {
                      ...(isLegacyIntValuedField ? ValidationRules.INTEGER : {}),
                      ...ValidationRules.LESS_THAN_OR_EQUAL_TO_UPPER_BOUND(maxValueConstraint.value),
                      ...ValidationRules.GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND(Number.MIN_SAFE_INTEGER),
                    }
                  : // If there's a max constraint, prevent the min value from going below it.
                    constraintIndex === maxValueConstraint.index && minValueConstraint.value != null
                    ? {
                        ...(isLegacyIntValuedField ? ValidationRules.INTEGER : {}),
                        ...ValidationRules.LESS_THAN_OR_EQUAL_TO_UPPER_BOUND(Number.MAX_SAFE_INTEGER),
                        ...ValidationRules.GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND(minValueConstraint.value),
                      }
                    : // If neither is set, let both values be effectively unbounded.
                      {
                        ...(isLegacyIntValuedField ? ValidationRules.INTEGER : {}),
                        ...ValidationRules.LESS_THAN_OR_EQUAL_TO_UPPER_BOUND(Number.MAX_SAFE_INTEGER),
                        ...ValidationRules.GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND(Number.MIN_SAFE_INTEGER),
                      },
            }}
            disabled={disabled}
            isDefaultMarginsOmitted={constraintIndex === constraints.length - 1}
            label={getValueConstraintDisplayName(row.constraint)}
          />
        );
      })}
    </>
  );
}

function parseBoolean(value: string) {
  if (value === "true") {
    return true;
  }
  if (value === "false") {
    return false;
  }
  return null;
}
